/**
 * Standortverträge in der Oberfläche: verhandeln, kündigen, umziehen.
 *
 * Die drei Dialoge gehören zusammen, weil sie derselben Frage nachgehen – zu
 * welchen Bedingungen stehen meine Geräte irgendwo? – und sie liegen bewusst
 * neben der Rechenzentrumsansicht statt in ihr: Die Karte eines Standorts ist
 * schon voll mit Strom, Kühlung und Racks.
 *
 * Ein Gedanke zieht sich durch alle drei: Jede Zahl steht *vor* der
 * Entscheidung da, nicht danach. Was ein Vertrag kostet, was ein Ausstieg
 * kostet, was ein Umzug kostet – wer das erst nach dem Klick erfährt, klickt
 * beim nächsten Mal nicht mehr.
 */
import { useMemo, useState } from 'react';
import type { ColoNegotiation, Datacenter, DatacenterId, NoticeMode } from '@hosting-sim/core';
import {
  COLOCATION,
  LOCATIONS,
  RELOCATION,
  SIM,
  checkMovable,
  checkTransportable,
  estimateTransport,
  findLocation,
  footprintFor,
  planSiteRelocation,
  quoteColocation,
  quoteNotice,
} from '@hosting-sim/core';
import { Badge, Button, Field, Input, Meter, Modal, Notice, Select, StatusDot } from './ui';
import { useGame } from '../store/game';
import { dec, duration, money, num } from '../lib/format';

/** Restlaufzeit eines Vertrags in Monaten, kaufmännisch gerundet. */
function monthsLeft(tick: number, until: number): number {
  return Math.max(0, Math.round(((until - tick) / (24 * 30)) * 10) / 10);
}

// ---------------------------------------------------------------------------
// Vertragsübersicht auf der Standortkarte
// ---------------------------------------------------------------------------

/**
 * Der Vertrag als Zeile in der Standortkarte.
 *
 * Zeigt drei Dinge, und zwar in dieser Reihenfolge, weil sie so gebraucht
 * werden: Was zahle ich, wie lange hänge ich fest, und – falls gekündigt – wie
 * viel Zeit bleibt zum Räumen.
 */
export function ContractPanel({ dc }: { dc: Datacenter }) {
  const world = useGame((s) => s.world);
  const [negotiating, setNegotiating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [moving, setMoving] = useState(false);

  if (!world) return null;
  const contract = dc.contract;
  if (!contract) return null;

  const tick = world.meta.tick;
  const notice = contract.notice;
  const saved = contract.listRent > 0 ? 1 - contract.monthlyRent / contract.listRent : 0;
  const bound = contract.termMonths > 0 && contract.termEndsAtTick > tick;

  return (
    <>
      <div className="mt-4 border-t divider pt-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="label mb-1">Standortvertrag</p>
            <p className="text-xs text-panel-600 dark:text-panel-300">
              {contract.providerName} · {contract.committedKw} kW zugesagt
              {contract.prepaidMonths > 0 && ` · ${contract.prepaidMonths} Monate vorausbezahlt`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setNegotiating(true)}>
              Neu verhandeln
            </Button>
            <Button size="sm" onClick={() => setMoving(true)}>
              Umziehen
            </Button>
            {notice ? (
              <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}>
                Kündigung ansehen
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}>
                Kündigen
              </Button>
            )}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Miete">
            {money(contract.monthlyRent)}
            {saved > 0.005 && <span className="ml-1 text-2xs text-accent-600 dark:text-accent-400">−{Math.round(saved * 100)} %</span>}
          </Field>
          <Field label="Bindung">
            {bound ? `noch ${dec(monthsLeft(tick, contract.termEndsAtTick), 1)} Monate` : 'monatlich kündbar'}
          </Field>
          <Field label="Kündigungsfrist">{contract.noticeMonths} Monate</Field>
          <Field label="Ausstieg">
            {contract.exitPenaltyShare > 0 ? `${Math.round(contract.exitPenaltyShare * 100)} % der Restmiete` : 'ohne Zahlung'}
          </Field>
        </div>

        {notice && (
          <div className="mt-3">
            <Notice tone={notice.effectiveAtTick - tick < 24 * 30 ? 'danger' : 'warning'} title="Dieser Standort ist gekündigt">
              Geräumt sein muss er in {duration(notice.effectiveAtTick - tick)}. Was dann noch dasteht, verwertet{' '}
              {contract.providerName} auf eigene Rechnung – die Kunden darauf sind damit weg.
            </Notice>
          </div>
        )}
      </div>

      {negotiating && (
        <NegotiationDialog locationKey={locationKeyOf(dc)} datacenterId={dc.id} onClose={() => setNegotiating(false)} />
      )}
      {cancelling && <NoticeDialog datacenterId={dc.id} onClose={() => setCancelling(false)} />}
      {moving && <SiteRelocationDialog fromDatacenterId={dc.id} onClose={() => setMoving(false)} />}
    </>
  );
}

/**
 * Standortschlüssel eines bestehenden Rechenzentrums.
 *
 * Der Weltzustand führt die Stadt, nicht den Katalogschlüssel – die Stadt ist
 * die Identität eines Standorts, der Schlüssel ein Detail des Katalogs. Für die
 * Verhandlung wird er trotzdem gebraucht, also wird er hier zurückgesucht.
 */
function locationKeyOf(dc: Datacenter): string {
  return LOCATIONS.find((location) => location.city === dc.city)?.key ?? '';
}

// ---------------------------------------------------------------------------
// Verhandlung
// ---------------------------------------------------------------------------

/**
 * Das Verhandlungsgespräch.
 *
 * Links die Stellschrauben, rechts das Angebot – und darunter der Verlauf.
 * Wichtig ist die Vorschau: Solange kein Gespräch läuft, rechnet der Dialog mit
 * derselben Formel wie der Anbieter. Wer die Laufzeit verdoppelt, sieht sofort,
 * was das bringt, statt es erst zu erfragen.
 */
export function NegotiationDialog({
  locationKey,
  datacenterId,
  onClose,
}: {
  locationKey: string;
  datacenterId?: DatacenterId | null;
  onClose: () => void;
}) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);

  const location = findLocation(locationKey);
  const running: ColoNegotiation | undefined = useMemo(
    () => (world ? Object.values(world.negotiations).find((n) => n.state === 'open' && n.locationKey === locationKey) : undefined),
    [world, locationKey],
  );

  /*
   * Eine Absage wirkt nach: Der Anbieter redet einige Tage nicht mehr über
   * diesen Standort. Das steht hier, damit niemand den Knopf drückt und eine
   * Fehlermeldung als Fehler missversteht.
   */
  const blocked = useMemo(
    () =>
      world
        ? Object.values(world.negotiations).find(
            (n) => n.locationKey === locationKey && n.state === 'failed' && world.meta.tick - n.startedAtTick < COLOCATION.COOLDOWN_TICKS,
          )
        : undefined,
    [world, locationKey],
  );

  const existing = datacenterId && world ? world.datacenters[datacenterId] : undefined;
  const [termMonths, setTermMonths] = useState<number>(existing?.contract.termMonths || 24);
  const [committedKw, setCommittedKw] = useState<number>(existing?.contract.committedKw ?? 0);
  const [prepaidMonths, setPrepaidMonths] = useState<number>(0);
  const [demanded, setDemanded] = useState<string>('');

  if (!world || !location) {
    return (
      <Modal open onClose={onClose} title="Standortvertrag">
        <Notice tone="warning">Zu diesem Standort liegen keine Katalogdaten vor.</Notice>
      </Modal>
    );
  }

  const minKw = Math.max(45, Math.floor(location.powerCapacityKw / 8));
  const maxKw = Math.max(minKw * 2, Math.floor(location.powerCapacityKw / 2));
  const kw = committedKw > 0 ? committedKw : minKw;

  const preview = quoteColocation(world, location, { termMonths, committedKw: kw, prepaidMonths });

  return (
    <Modal open onClose={onClose} wide title={`Colocation ${location.city}`}>
      {running ? (
        <div className="space-y-4">
          <div className="surface-muted p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-medium">{running.providerName}</p>
              <Badge tone="info">
                Runde {running.round} von {running.maxRounds}
              </Badge>
            </div>
            <p className="mt-1 text-xs text-panel-600 dark:text-panel-300">{running.note}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Angebot">{money(running.offer.monthlyRent)}/Mon.</Field>
            <Field label="Listenpreis">{money(running.listRent)}/Mon.</Field>
            <Field label="Laufzeit">{running.offer.termMonths > 0 ? `${running.offer.termMonths} Monate` : 'monatlich'}</Field>
            <Field label="Einmalig">{money(running.offer.setupCost + running.offer.monthlyRent * running.offer.prepaidMonths)}</Field>
          </div>

          <Meter
            value={running.patience}
            max={1}
            invert
            label={
              <>
                <span>Geduld des Anbieters</span>
                <span className="tabular">{Math.round(running.patience * 100)} %</span>
              </>
            }
          />

          <div className="flex flex-wrap items-end gap-3">
            <div className="w-40">
              <Input
                label="Gegenangebot (€/Mon.)"
                type="number"
                value={demanded}
                placeholder={String(Math.round(running.offer.monthlyRent * 0.9))}
                onChange={(e) => setDemanded(e.target.value)}
              />
            </div>
            <Button
              onClick={() =>
                void run({
                  type: 'coloc/counter',
                  negotiationId: running.id,
                  demandedRent: demanded ? Number(demanded) : undefined,
                })
              }
              disabled={running.round >= running.maxRounds}
            >
              Nachfassen
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                const result = await run({ type: 'coloc/accept', negotiationId: running.id });
                if (result.ok) onClose();
              }}
            >
              Annehmen
            </Button>
            <Button
              variant="ghost"
              onClick={async () => {
                await run({ type: 'coloc/abort', negotiationId: running.id });
                onClose();
              }}
            >
              Abbrechen
            </Button>
          </div>

          <Notice tone="info">
            Jede Nachforderung kostet Geduld – maßvolle wenig, dreiste viel. Unter dem Selbstkostenpreis des Anbieters ist Schluss;
            wer ihn deutlich unterbietet, beendet das Gespräch.
          </Notice>
        </div>
      ) : (
        <div className="space-y-4">
          {blocked ? (
            <Notice tone="warning" title="Das Gespräch ist geplatzt">
              {blocked.note} {blocked.providerName} redet über {location.city} erst in{' '}
              {duration(blocked.startedAtTick + COLOCATION.COOLDOWN_TICKS - world.meta.tick)} wieder.
            </Notice>
          ) : (
            <Notice tone="info">
              {datacenterId
                ? 'Ein bestehender Vertrag lässt sich jederzeit neu verhandeln. Der neue ersetzt den alten – mit neuer Bindung.'
                : 'Vor der Eröffnung wird über die Konditionen gesprochen. Wer sofort eröffnet, zahlt den Listenpreis.'}
            </Notice>
          )}

          <div className="grid gap-3 sm:grid-cols-3">
            <Select label="Laufzeit" value={termMonths} onChange={(e) => setTermMonths(Number(e.target.value))}>
              {COLOCATION.TERMS.map((term) => (
                <option key={term} value={term}>
                  {term === 0 ? 'ohne Bindung' : `${term} Monate`}
                </option>
              ))}
            </Select>
            <Input
              label="Zusage (kW)"
              type="number"
              min={minKw}
              max={maxKw}
              step={5}
              value={kw}
              onChange={(e) => setCommittedKw(Number(e.target.value))}
              hint={`${minKw} bis ${maxKw} kW`}
            />
            <Input
              label="Vorauszahlung (Monate)"
              type="number"
              min={0}
              max={COLOCATION.MAX_PREPAID_MONTHS}
              value={prepaidMonths}
              onChange={(e) => setPrepaidMonths(Number(e.target.value))}
              hint={`bis ${COLOCATION.MAX_PREPAID_MONTHS} Monate`}
            />
          </div>

          <div className="surface-muted p-3">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Erwartete Miete">{money(preview.monthlyRent)}/Mon.</Field>
              <Field label="Listenpreis">{money(preview.listRent)}/Mon.</Field>
              <Field label="Sofort fällig">{money(preview.upfront)}</Field>
              <Field label="Rackplätze">{num(footprintFor(location, kw).rackCapacity)}</Field>
            </div>

            <ul className="mt-3 space-y-1">
              {preview.breakdown.map((entry) => (
                <li key={entry.label} className="flex items-center justify-between text-xs">
                  <span className="text-panel-600 dark:text-panel-300">{entry.label}</span>
                  <span className={entry.share >= 0 ? 'tabular text-accent-600 dark:text-accent-400' : 'tabular text-amber-600 dark:text-amber-400'}>
                    {entry.share >= 0 ? '−' : '+'}
                    {Math.abs(Math.round(entry.share * 1000) / 10)} %
                  </span>
                </li>
              ))}
              <li className="flex items-center justify-between border-t divider pt-1 text-xs font-medium">
                <span>Untergrenze des Anbieters</span>
                <span className="tabular">{money(preview.floorRent)}/Mon.</span>
              </li>
            </ul>

            <p className="mt-3 text-2xs text-panel-500">
              Kündigungsfrist {preview.noticeMonths} Monate, vorzeitiger Ausstieg{' '}
              {preview.exitPenaltyShare > 0 ? `${Math.round(preview.exitPenaltyShare * 100)} % der Restmiete` : 'kostenlos'}.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              disabled={!!blocked}
              onClick={() =>
                void run({
                  type: 'coloc/negotiate',
                  locationKey,
                  termMonths,
                  committedKw: kw,
                  prepaidMonths,
                  datacenterId: datacenterId ?? null,
                })
              }
            >
              Angebot einholen
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Kündigung
// ---------------------------------------------------------------------------

/**
 * Kündigen – mit beiden Wegen nebeneinander.
 *
 * Zum Laufzeitende ist kostenlos und dauert; sofort kostet die Restlaufzeit.
 * Beide Zahlen stehen gleichzeitig da, weil die Entscheidung genau zwischen
 * ihnen liegt.
 */
export function NoticeDialog({ datacenterId, onClose }: { datacenterId: DatacenterId; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [mode, setMode] = useState<NoticeMode>('term');

  if (!world) return null;
  const dc = world.datacenters[datacenterId];
  if (!dc) return null;

  const existing = dc.contract.notice;
  const angebote: { mode: NoticeMode; titel: string; quote: ReturnType<typeof quoteNotice> }[] = [
    { mode: 'term', titel: 'Zum Ende der Laufzeit', quote: quoteNotice(world, datacenterId, 'term') },
    { mode: 'early', titel: 'Vorzeitig, mit Frist', quote: quoteNotice(world, datacenterId, 'early') },
    { mode: 'immediate', titel: 'Sofort raus', quote: quoteNotice(world, datacenterId, 'immediate') },
  ];
  const chosen = angebote.find((a) => a.mode === mode)!.quote;

  return (
    <Modal open onClose={onClose} wide title={`${dc.name} kündigen`}>
      {existing ? (
        <div className="space-y-4">
          <Notice tone="warning" title="Bereits gekündigt">
            Der Vertrag endet in {duration(existing.effectiveAtTick - world.meta.tick)}
            {existing.penaltyPaid > 0 && ` – die Ausstiegszahlung von ${money(existing.penaltyPaid)} ist gebucht`}.
          </Notice>
          <SiteInventory datacenterId={datacenterId} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Schließen
            </Button>
            <Button
              onClick={async () => {
                const result = await run({ type: 'coloc/withdrawNotice', datacenterId });
                if (result.ok) onClose();
              }}
            >
              Kündigung zurücknehmen
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <SiteInventory datacenterId={datacenterId} />

          {/*
            Drei Wege statt zwei. Der alte Knopf hieß „Sofort, mit Frist“ und
            widersprach sich damit selbst: Wer „sofort“ liest, erwartet nicht,
            zwei Monate weiterzuzahlen. Jetzt steht beides getrennt da – und
            „sofort“ heißt tatsächlich heute.
          */}
          <div className="grid gap-3 sm:grid-cols-3">
            {angebote.map(({ mode: option, titel, quote }) => (
              <button
                key={option}
                type="button"
                onClick={() => setMode(option)}
                className={`surface-muted p-3 text-left transition ${mode === option ? 'ring-1 ring-accent-500' : ''}`}
              >
                <p className="text-sm font-medium">{titel}</p>
                <p className="mt-1 text-xs text-panel-500 dark:text-panel-400">
                  {option === 'term' &&
                    `Endet in ${duration(quote.ticksUntil)}, ohne Zahlung. Bis dahin bleibt der Standort in Betrieb.`}
                  {option === 'early' &&
                    `Endet in ${duration(quote.ticksUntil)} – die Kündigungsfrist läuft. Die Restlaufzeit wird anteilig fällig.`}
                  {option === 'immediate' &&
                    'Endet heute. Zusätzlich zur Restlaufzeit wird die nicht abgewohnte Kündigungsfrist voll fällig.'}
                </p>
                <p className="mt-2 text-sm font-medium tabular">{quote.penalty > 0 ? money(quote.penalty) : 'ohne Zahlung'}</p>
                {option === 'immediate' && quote.penalty > 0 && (
                  <p className="mt-1 text-2xs text-panel-500">
                    {money(quote.termBuyout)} Restlaufzeit + {money(quote.noticeBuyout)} Frist
                  </p>
                )}
              </button>
            ))}
          </div>

          {chosen.devices > 0 && mode !== 'immediate' && (
            <Notice tone="warning" title="Erst räumen, dann kündigen">
              Bis zum Stichtag müssen {chosen.devices} Geräte umgezogen oder abgegeben sein. Was stehen bleibt, verwertet der
              Anbieter zum Schrottpreis – samt der Kunden darauf.
            </Notice>
          )}

          {chosen.devices > 0 && mode === 'immediate' && (
            <Notice tone="danger" title={`${chosen.devices} Geräte stehen noch dort`}>
              Bei einem Ausstieg am selben Tag bleibt keine Zeit zum Räumen: Die Geräte gehen mit dem Standort an den
              Anbieter, zum Schrottpreis, samt der Kunden darauf. Wer die Hardware behalten will, wählt „vorzeitig“ und zieht
              in der Frist um.
            </Notice>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                const result = await run({ type: 'coloc/giveNotice', datacenterId, mode });
                if (result.ok) onClose();
              }}
            >
              {chosen.penalty > 0 ? `Kündigen für ${money(chosen.penalty)}` : 'Kündigen'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Was an einem Standort steht – die Grundlage jeder Räumungsentscheidung. */
function SiteInventory({ datacenterId }: { datacenterId: DatacenterId }) {
  const world = useGame((s) => s.world);
  if (!world) return null;

  const at = <T extends { datacenterId: string }>(store: Record<string, T>) => Object.values(store).filter((d) => d.datacenterId === datacenterId);
  const servers = at(world.servers);
  const contracts = servers.reduce(
    (sum, s) => sum + s.contractIds.filter((id) => world.contracts[id]?.state !== 'cancelled').length + s.vpsIds.length,
    0,
  );
  const devices =
    servers.length + at(world.routers).length + at(world.switches).length + at(world.firewalls).length + at(world.loadBalancers).length + at(world.upsUnits).length;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Field label="Geräte vor Ort">{num(devices)}</Field>
      <Field label="davon Server">{num(servers.length)}</Field>
      <Field label="Kundendienste">
        <span className={contracts > 0 ? 'text-amber-600 dark:text-amber-400' : ''}>{num(contracts)}</span>
      </Field>
      <Field label="Racks">{num(Object.values(world.racks).filter((r) => r.datacenterId === datacenterId).length)}</Field>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Einzelnes Gerät umsetzen
// ---------------------------------------------------------------------------

/**
 * Ein einzelnes Gerät in ein anderes Rechenzentrum fahren.
 *
 * Der Knopf sitzt dort, wo der Gedanke entsteht – am Gerät selbst. Wer erst
 * eine Umzugsübersicht suchen und das Gerät dort wiederfinden müsste, verschiebt
 * einen Server nie.
 */
export function RelocateDeviceButton({
  kind,
  id,
  name,
}: {
  kind: 'server' | 'router' | 'switch' | 'firewall' | 'loadbalancer' | 'ups';
  id: string;
  name: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        Umsetzen
      </Button>
      {open && <RelocateDeviceDialog kind={kind} id={id} name={name} onClose={() => setOpen(false)} />}
    </>
  );
}

function RelocateDeviceDialog({
  kind,
  id,
  name,
  onClose,
}: {
  kind: 'server' | 'router' | 'switch' | 'firewall' | 'loadbalancer' | 'ups';
  id: string;
  name: string;
  onClose: () => void;
}) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [rackId, setRackId] = useState('');
  const [forceOutage, setForceOutage] = useState(false);

  if (!world) return null;
  const device = deviceIn(world, kind, id);

  /*
   * Beide Wege in einer Auswahl. Aus Sicht des Spielers ist es eine Frage –
   * „wohin damit?“ –, und erst das gewählte Rack entscheidet, ob ein Lastwagen
   * fährt oder jemand zwei Racks weiter geht. Zwei getrennte Knöpfe zwängen ihm
   * eine Unterscheidung auf, die das Spiel selbst treffen kann.
   */
  const hier = Object.values(world.racks).filter((rack) => rack.datacenterId === device?.datacenterId && rack.id !== device?.rackId);
  const anderswo = Object.values(world.racks).filter((rack) => rack.datacenterId !== device?.datacenterId);
  const targets = [...hier, ...anderswo];
  const chosen = rackId || targets[0]?.id || '';
  const umbau = hier.some((rack) => rack.id === chosen);

  // Beim Umbau bleibt die Verkabelung; die Transportprüfung („erst umstecken“)
  // gilt dort nicht. Was bleibt, ist der Ausfall belegter Server.
  const check = umbau ? checkMovable(world, kind, id, chosen as never) : checkTransportable(world, kind, id);
  const estimate = !umbau && chosen ? estimateTransport(world, { kind, id, targetRackId: chosen as never }) : null;

  return (
    <Modal open onClose={onClose} title={`${name} umsetzen`}>
      <div className="space-y-4">
        {targets.length === 0 ? (
          <Notice tone="warning" title="Kein Ziel vorhanden">
            Es gibt kein zweites Rack, in das dieses Gerät passen könnte – weder hier noch an einem anderen Standort. Erst ein
            Rack anmieten.
          </Notice>
        ) : (
          <>
            <Select label="Zielrack" value={chosen} onChange={(e) => setRackId(e.target.value)}>
              {hier.length > 0 && (
                <optgroup label="Hier vor Ort – Umbau">
                  {hier.map((rack) => (
                    <option key={rack.id} value={rack.id}>
                      {rack.name}
                    </option>
                  ))}
                </optgroup>
              )}
              {anderswo.length > 0 && (
                <optgroup label="Anderer Standort – Transport">
                  {anderswo.map((rack) => (
                    <option key={rack.id} value={rack.id}>
                      {world.datacenters[rack.datacenterId]?.city} – {rack.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </Select>

            {!check.ok && <Notice tone="warning">{check.reason}</Notice>}

            {umbau && (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Field label="Spedition">entfällt</Field>
                  <Field label="Außer Betrieb">{duration(RELOCATION.INSTALL_TICKS + SIM.BOOT_TICKS)}</Field>
                  <Field label="Verkabelung">bleibt gesteckt</Field>
                </div>
                <Notice tone="info" title="Kein Lastwagen, nur Schraubenzieher">
                  Zwei Racks im selben Raum verbindet ein längeres Kabel. Der Uplink bleibt, die Strecken bleiben – das Gerät
                  ist nur für den Aus- und Einbau weg.
                </Notice>
                {'needsForce' in check && check.needsForce && (
                  <label className="flex items-start gap-2 text-xs">
                    <input type="checkbox" className="mt-0.5" checked={forceOutage} onChange={(e) => setForceOutage(e.target.checked)} />
                    <span>
                      Ausfall in Kauf nehmen: Die Dienste auf diesem Server sind während des Umbaus offline. Das zählt als
                      Ausfallzeit und kann SLA-Gutschriften auslösen.
                    </span>
                  </label>
                )}
              </>
            )}

            {estimate?.ok && (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Field label="Entfernung">{num(estimate.distanceKm)} km</Field>
                  <Field label="Spedition">{money(estimate.cost)}</Field>
                  <Field label="Außer Betrieb">{duration(estimate.ticks)}</Field>
                  <Field label="Schadensrisiko">{Math.round(estimate.damageRisk * 100)} %</Field>
                </div>
                {estimate.cost > estimate.residualValue && (
                  <Notice tone="info" title="Rechnet sich das?">
                    Der Transport kostet {money(estimate.cost)}, das Gerät ist noch {money(estimate.residualValue)} wert. Verkaufen
                    und drüben neu kaufen wäre günstiger – und brächte neuere Hardware.
                  </Notice>
                )}
              </>
            )}
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            disabled={!chosen || (!check.ok && !('needsForce' in check && check.needsForce && forceOutage))}
            onClick={async () => {
              const result = await run(
                umbau
                  ? { type: 'dc/moveDevice', kind, id, targetRackId: chosen as never, force: forceOutage }
                  : { type: 'dc/relocateDevice', kind, id, targetRackId: chosen as never },
              );
              if (result.ok) onClose();
            }}
          >
            {umbau ? 'Umbauen' : 'Fahren lassen'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Ein Gerät aus dem passenden Speicher – für Standort, Rack und Namen. */
function deviceIn(
  world: NonNullable<ReturnType<typeof useGame.getState>['world']>,
  kind: string,
  id: string,
): { datacenterId: string; rackId: string | null } | undefined {
  const stores: Record<string, Record<string, { datacenterId: string; rackId: string | null }>> = {
    server: world.servers,
    router: world.routers,
    switch: world.switches,
    firewall: world.firewalls,
    loadbalancer: world.loadBalancers,
    ups: world.upsUnits,
  };
  return stores[kind]?.[id];
}

// ---------------------------------------------------------------------------
// Standortumzug
// ---------------------------------------------------------------------------

/**
 * Der Umzug eines ganzen Standorts.
 *
 * Der Plan steht vor dem Knopf: welche Geräte fahren, was es kostet, wie lange
 * sie weg sind – und was sich schlicht nicht lohnt, weil der Transport teurer
 * ist als der Zeitwert.
 */
export function SiteRelocationDialog({ fromDatacenterId, onClose }: { fromDatacenterId: DatacenterId; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [target, setTarget] = useState<string>('');

  const others = world ? Object.values(world.datacenters).filter((dc) => dc.id !== fromDatacenterId) : [];
  const targetId = (target || others[0]?.id) as DatacenterId | undefined;
  const plan = useMemo(
    () => (world && targetId ? planSiteRelocation(world, fromDatacenterId, targetId) : null),
    [world, fromDatacenterId, targetId],
  );

  if (!world) return null;
  const from = world.datacenters[fromDatacenterId];

  return (
    <Modal open onClose={onClose} wide title={`${from?.city} räumen und umziehen`}>
      <div className="space-y-4">
        {others.length === 0 ? (
          <Notice tone="warning" title="Es gibt kein Ziel">
            Ein Umzug braucht einen zweiten Standort mit angemieteten Racks. Erst dort einen Vertrag schließen und ein Rack mieten,
            dann fahren die Geräte.
          </Notice>
        ) : (
          <>
            <Select label="Zielstandort" value={targetId ?? ''} onChange={(e) => setTarget(e.target.value)}>
              {others.map((dc) => (
                <option key={dc.id} value={dc.id}>
                  {dc.city} – {Object.values(world.racks).filter((r) => r.datacenterId === dc.id).length} Racks
                </option>
              ))}
            </Select>

            {plan?.error ? (
              <Notice tone="warning">{plan.error}</Notice>
            ) : (
              plan && (
                <>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Field label="Geräte">{num(plan.steps.length)}</Field>
                    <Field label="Speditionskosten">{money(plan.totalCost)}</Field>
                    <Field label="Längste Fahrt">{duration(plan.totalTicks)}</Field>
                    <Field label="Bleiben stehen">
                      <span className={plan.blocked.length > 0 ? 'text-amber-600 dark:text-amber-400' : ''}>{num(plan.blocked.length)}</span>
                    </Field>
                  </div>

                  {plan.notWorthIt > 0 && (
                    <Notice tone="info" title="Nicht alles lohnt die Fahrt">
                      Bei {plan.notWorthIt} {plan.notWorthIt === 1 ? 'Gerät' : 'Geräten'} liegt der Transportpreis über dem Zeitwert.
                      Verkaufen und drüben neu kaufen ist dort günstiger – und man bekommt neuere Hardware.
                    </Notice>
                  )}

                  {plan.steps.length > 0 && (
                    <div className="max-h-56 space-y-1 overflow-y-auto">
                      {plan.steps.map((step) => (
                        <div key={`${step.kind}-${step.id}`} className="surface-muted flex flex-wrap items-center gap-2 p-2 text-xs">
                          <Badge tone="muted">{step.kind}</Badge>
                          <span className="min-w-0 flex-1 truncate font-medium">{step.name}</span>
                          <span className="tabular text-panel-500">nach {step.targetRackName}</span>
                          <span className={`tabular ${step.cost > step.residualValue ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                            {money(step.cost)}
                          </span>
                          <span className="tabular text-panel-500">{duration(step.ticks)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {plan.blocked.length > 0 && (
                    <div className="space-y-1">
                      <p className="label">Bleibt stehen</p>
                      {plan.blocked.map((entry) => (
                        <div key={entry.name} className="flex items-start gap-2 text-2xs text-panel-500">
                          <StatusDot tone="warning" />
                          <span>
                            <strong>{entry.name}</strong> – {entry.reason}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )
            )}
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
          <Button
            variant="primary"
            disabled={!plan || plan.steps.length === 0}
            onClick={async () => {
              if (!targetId) return;
              const result = await run({ type: 'dc/relocateSite', fromDatacenterId, toDatacenterId: targetId });
              if (result.ok) onClose();
            }}
          >
            {plan && plan.steps.length > 0 ? `${plan.steps.length} Geräte fahren lassen` : 'Umzug starten'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
